// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseAvatar, Loading } from '~/components/common';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { getDetailJob } from '~/thunks/job/jobThunk';
import { PickSlipFormModal } from '~/components/module/order';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { RoleEnum, TypeJobDetailEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IDetailJob, IParamQueryJobDetail } from '~/utils/interface/jobs';
import { formatDate, formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './InfoJobTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const InfoJobTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: jobId } = useParams();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshJobDetail } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [detailJob, setDetailJob] = useState<IDetailJob>();
  const [isShowPickSplitModal, setIsShowPickSplitModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!jobId) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId,
      type: TypeJobDetailEnum.OVERVIEW,
    };

    handleGetDetailJob(params);
  }, [jobId]);

  useEffect(() => {
    if (!isRefreshJobDetail || !jobId) return;

    const params: IParamQueryJobDetail = {
      jobId: jobId,
      type: TypeJobDetailEnum.OVERVIEW,
    };

    handleGetDetailJob(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshJobDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailJob = (params: IParamQueryJobDetail) => {
    setIsLoading(true);

    dispatch(getDetailJob(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setDetailJob(res.data as IDetailJob);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderHeader = (title: string, icon?: React.ReactElement) => {
    return (
      <Stack direction={'row'} gap={2}>
        <>{icon}</>

        <Typography className={cx('title')}>{title}</Typography>
      </Stack>
    );
  };

  const renderItemValue = (title: string, value: string, icon?: React.ReactElement | null, onClick?: () => void) => {
    return (
      <Stack gap={2}>
        <Stack gap={2} direction={'row'}>
          {icon && <Box className={cx('icon')}>{icon}</Box>}

          <Typography className={cx('label')}>{title}</Typography>
        </Stack>

        <Typography className={cx('value', onClick && 'click')} onClick={onClick}>
          {value}
        </Typography>
      </Stack>
    );
  };

  const handleNavigateDetailOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailOrder(detailJob?.orderId || EMPTY_STRING));
        break;
    }
  };

  const handleShowFormPickSlip = () => {
    setIsShowPickSplitModal(!isShowPickSplitModal);
  };
  //#endregion Handle Function

  return (
    <Stack id='infoJobTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <Stack direction={'row'} gap={3} alignItems={'center'}>
          {/*<BaseAvatar*/}
          {/*  dataAccount={{*/}
          {/*    firstName: detailJob?.client?.firstName || EMPTY_STRING,*/}
          {/*    lastName: detailJob?.client?.lastName || EMPTY_STRING,*/}
          {/*    // avatarImage: detailJob?.client?.avatarImage || EMPTY_STRING,*/}
          {/*  }}*/}
          {/*  size={65}*/}
          {/*  fontSize={24}*/}
          {/*/>*/}

          <Typography className={cx('textName')}>
            {/*{getFullName({*/}
            {/*  firstName: detailJob?.client?.firstName || EMPTY_STRING,*/}
            {/*  lastName: detailJob?.client?.lastName || EMPTY_STRING,*/}
            {/*})}*/}
            {detailJob?.title || EMPTY_STRING}
          </Typography>
        </Stack>

        <Button variant='contained' onClick={handleShowFormPickSlip}>
          {t('job_detail_title_pick_slip')}
        </Button>
      </Stack>

      <Grid2 container spacing={8} padding={7}>
        <Grid2 size={6}>
          <Grid2 container spacing={5}>
            <Grid2 size={7}>
              <Stack gap={'15px'}>
                <>
                  {renderHeader(t('job_detail_title_job_details'))}

                  {renderItemValue(
                    t('job_detail_title_order_id'),
                    detailJob?.orderId ? `#${detailJob?.orderId}` : EMPTY_VALUE,
                    null,
                    handleNavigateDetailOrder
                  )}

                  {renderItemValue(
                    t('job_detail_title_client_name'),
                    getFullName({
                      firstName: detailJob?.client?.firstName || EMPTY_STRING,
                      lastName: detailJob?.client?.lastName || EMPTY_STRING,
                    }) || EMPTY_VALUE
                  )}

                  {renderItemValue(
                    t('job_detail_title_client_phone'),
                    formatPhoneNumber(detailJob?.client?.clientContacts?.[0].phoneNumber) || EMPTY_VALUE
                  )}

                  {renderItemValue(t('job_detail_title_date_time'), formatDate(detailJob?.startDate) || EMPTY_VALUE)}

                  {renderItemValue(t('job_detail_title_sales_person'), EMPTY_VALUE)}
                </>
              </Stack>
            </Grid2>

            <Grid2 size={5}>
              <Stack gap={'15px'}>
                {renderHeader(
                  t('job_detail_title_address'),
                  <img src={icons.commonIconLocation} alt={t('common_img_text_alt')} />
                )}

                {renderItemValue(
                  t('job_detail_title_street_address'),
                  detailJob?.address?.address || EMPTY_VALUE,
                  <img src={icons.commonIconHome} alt={t('common_img_text_alt')} />
                )}

                <>
                  {renderHeader(t('job_detail_title_crew_members'))}

                  {detailJob?.crews && detailJob?.crews?.length > 0 ? (
                    detailJob?.crews?.map((item) => (
                      <Stack direction={'row'} gap={4} alignItems={'center'} key={item.id}>
                        <BaseAvatar dataAccount={item} size={32} fontSize={14} />
                        <Stack direction={'column'}>
                          <Typography className={cx('textName')}>
                            {getFullName({
                              firstName: item?.firstName,
                              lastName: item?.lastName,
                            })}
                          </Typography>
                          <Typography className={cx('textEmail')}>{item?.email}</Typography>
                        </Stack>
                      </Stack>
                    ))
                  ) : (
                    <Typography className={cx('value')}>{EMPTY_VALUE}</Typography>
                  )}
                </>

                <>
                  {renderHeader(t('job_detail_title_loader_members'))}

                  {detailJob?.loader && detailJob?.loader?.length > 0 ? (
                    detailJob?.loader?.map((item) => (
                      <Stack direction={'row'} gap={4} alignItems={'center'} key={item.id}>
                        <BaseAvatar dataAccount={item} size={32} fontSize={14} />
                        <Stack direction={'column'}>
                          <Typography className={cx('textName')}>
                            {getFullName({
                              firstName: item?.firstName,
                              lastName: item?.lastName,
                            })}
                          </Typography>
                          <Typography className={cx('textEmail')}>{item?.email}</Typography>
                        </Stack>
                      </Stack>
                    ))
                  ) : (
                    <Typography className={cx('value')}>{EMPTY_VALUE}</Typography>
                  )}
                </>
              </Stack>
            </Grid2>

            <Grid2 size={12}>{renderHeader(t('job_detail_title_team_information'))}</Grid2>

            <Grid2 size={7}>
              <Stack gap={'15px'}>
                {renderItemValue(
                  t('job_detail_title_foreman'),
                  getFullName({
                    firstName: detailJob?.foreman?.firstName || EMPTY_STRING,
                    lastName: detailJob?.foreman?.lastName || EMPTY_STRING,
                  }) || EMPTY_VALUE
                )}

                {renderItemValue(
                  t('job_detail_title_driver'),
                  getFullName({
                    firstName: detailJob?.driver?.firstName || EMPTY_STRING,
                    lastName: detailJob?.driver?.lastName || EMPTY_STRING,
                  }) || EMPTY_VALUE
                )}
              </Stack>
            </Grid2>

            <Grid2 size={5}>
              <Stack gap={'15px'}>
                {renderItemValue(t('job_detail_title_truck_number'), detailJob?.truck?.name || EMPTY_VALUE)}
                {renderItemValue(t('job_detail_title_trailer_number'), detailJob?.trailer?.name || EMPTY_VALUE)}
              </Stack>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={6}>
          <Stack gap={4}>
            {renderHeader(t('job_detail_title_additional_notes'))}

            <Box className={cx('viewBox')}>{detailJob?.note || EMPTY_VALUE}</Box>
          </Stack>
        </Grid2>
      </Grid2>

      {isShowPickSplitModal && jobId && <PickSlipFormModal jobId={jobId} onClose={handleShowFormPickSlip} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default InfoJobTab;
